import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

export const PRESETS = [
  { id: "aurora",    label: "Aurora",     from: "#4F46E5", via: "#7C3AED", to: "#F43F5E" },
  { id: "sunset",    label: "Sunset",     from: "#F97316", via: "#E11D48", to: "#7C3AED" },
  { id: "ocean",     label: "Ocean",      from: "#0EA5E9", via: "#4F46E5", to: "#0F172A" },
  { id: "meadow",    label: "Meadow",     from: "#059669", via: "#10B981", to: "#0EA5E9" },
  { id: "rose-gold", label: "Rose gold",  from: "#F43F5E", via: "#F59E0B", to: "#B45309" },
  { id: "midnight",  label: "Midnight",   from: "#0F172A", via: "#4F46E5", to: "#7C3AED" },
  { id: "candy",     label: "Candy",      from: "#EC4899", via: "#8B5CF6", to: "#0EA5E9" },
  { id: "citrus",    label: "Citrus",     from: "#F59E0B", via: "#F97316", to: "#EF4444" },
];

export const HEADING_FONTS = [
  { id: "cabinet", label: "Cabinet Grotesk", stack: "'Cabinet Grotesk', ui-sans-serif, system-ui, sans-serif" },
  { id: "playfair", label: "Playfair Display", stack: "'Playfair Display', Georgia, serif", url: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;800&display=swap" },
  { id: "space", label: "Space Grotesk", stack: "'Space Grotesk', ui-sans-serif, sans-serif", url: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&display=swap" },
  { id: "fraunces", label: "Fraunces", stack: "'Fraunces', Georgia, serif", url: "https://fonts.googleapis.com/css2?family=Fraunces:wght@600;900&display=swap" },
];
export const RADII = [
  { id: "soft", label: "Soft", radius: "0.75rem" },
  { id: "rounded", label: "Rounded", radius: "0.5rem" },
  { id: "sharp", label: "Sharp", radius: "0.125rem" },
];

const DEFAULT = { ...PRESETS[0], headingFontId: "cabinet", radiusId: "rounded", mode: "light" };
const LS = "aceh_theme_v2";
const LS_SAVED = "aceh_saved_themes_v1";
const ThemeCtx = createContext(null);

function loadFontLink(url) {
  if (!url || document.querySelector(`link[href="${url}"]`)) return;
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = url; document.head.appendChild(l);
}

function applyVars(theme) {
  const r = document.documentElement.style;
  r.setProperty("--hero-from", theme.from);
  r.setProperty("--hero-via", theme.via);
  r.setProperty("--hero-to", theme.to);
  const font = HEADING_FONTS.find(f => f.id === theme.headingFontId) || HEADING_FONTS[0];
  if (font.url) loadFontLink(font.url);
  r.setProperty("--font-heading", font.stack);
  const rad = RADII.find(x => x.id === theme.radiusId) || RADII[1];
  r.setProperty("--radius", rad.radius);
  // Dark mode class on <html>
  const dark = theme.mode === "dark";
  document.documentElement.classList.toggle("dark", dark);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(LS)); if (s && s.from) return { ...DEFAULT, ...s }; } catch {}
    return DEFAULT;
  });
  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_SAVED)) || []; } catch { return []; }
  });
  const [teamTheme, setTeamThemeState] = useState(null);

  useEffect(() => { applyVars(theme); localStorage.setItem(LS, JSON.stringify(theme)); }, [theme]);
  useEffect(() => { localStorage.setItem(LS_SAVED, JSON.stringify(saved)); }, [saved]);
  useEffect(() => {
    // Load team theme in background; user preference wins unless user hasn't customized
    api.get("/settings/team-theme").then(({ data }) => {
      if (data) {
        setTeamThemeState(data);
        const hasUser = !!localStorage.getItem(LS);
        if (!hasUser) setTheme((t) => ({ ...t, ...data, mode: t.mode }));
      }
    }).catch(() => {});
  }, []);

  const setPreset = (id) => { const p = PRESETS.find(x => x.id === id); if (p) setTheme((t) => ({ ...t, ...p })); };
  const setCustom = (partial) => setTheme((t) => ({ ...t, id: "custom", label: "Custom", ...partial }));
  const setHeadingFont = (id) => setTheme((t) => ({ ...t, headingFontId: id }));
  const setRadius = (id) => setTheme((t) => ({ ...t, radiusId: id }));
  const setMode = (mode) => setTheme((t) => ({ ...t, mode }));
  const toggleMode = () => setTheme((t) => ({ ...t, mode: t.mode === "dark" ? "light" : "dark" }));
  const saveCurrent = (name) => setSaved((s) => [...s.filter(x => x.name !== name), { name, theme: { ...theme } }].slice(-8));
  const applySaved = (name) => { const it = saved.find(x => x.name === name); if (it) setTheme(it.theme); };
  const removeSaved = (name) => setSaved((s) => s.filter(x => x.name !== name));
  const publishTeamTheme = async () => {
    await api.put("/settings/team-theme", theme);
    setTeamThemeState({ ...theme });
  };

  return (
    <ThemeCtx.Provider value={{ theme, setTheme, setPreset, setCustom, setHeadingFont, setRadius, setMode, toggleMode, saved, saveCurrent, applySaved, removeSaved, teamTheme, publishTeamTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
